"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useLocation } from "@/components/location/LocationProvider";
import {
  getNormalizedLocation,
  hasCoordinates,
  hasUsableLocationFilter,
  haversineDistanceKm,
} from "@/lib/location/filter";
import type { BrowseMode } from "@/lib/browse/getHomeBrowseData";
import EditorialBusinessCard, {
  type EditorialBusiness,
} from "./EditorialBusinessCard";
import LazyAmbientVideo from "./LazyAmbientVideo";
import styles from "./HomepageEditorial.module.css";

const KM_TO_MILES = 0.621371;

function buildBusinessQuery(
  location: ReturnType<typeof getNormalizedLocation>
) {
  const params = new URLSearchParams();
  if (location.city) params.set("city", location.city);
  if (location.state) params.set("state", location.state);
  if (typeof location.lat === "number") params.set("lat", String(location.lat));
  if (typeof location.lng === "number") params.set("lng", String(location.lng));
  return params.toString();
}

function parseCoordinate(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function selectEditorialBusinesses(
  businesses: EditorialBusiness[],
  location: ReturnType<typeof getNormalizedLocation>,
  limit = 3
) {
  const seen = new Set<string>();
  const canMeasureDistance = hasCoordinates(location);

  return (Array.isArray(businesses) ? businesses : [])
    .map((business) => {
      const lat = parseCoordinate(
        (business as EditorialBusiness & { latitude?: unknown; lat?: unknown })
          .latitude ??
          (business as EditorialBusiness & { lat?: unknown }).lat
      );
      const lng = parseCoordinate(
        (business as EditorialBusiness & { longitude?: unknown; lng?: unknown })
          .longitude ??
          (business as EditorialBusiness & { lng?: unknown }).lng
      );
      const distanceKm =
        canMeasureDistance && lat !== null && lng !== null
          ? haversineDistanceKm(location, { lat, lng })
          : null;

      return {
        ...business,
        distanceMiles:
          typeof distanceKm === "number" && Number.isFinite(distanceKm)
            ? distanceKm * KM_TO_MILES
            : business.distanceMiles ?? null,
      };
    })
    .filter((business) => {
      const key = String(business?.public_id || "").trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => {
      const leftDistance = left.distanceMiles ?? Number.POSITIVE_INFINITY;
      const rightDistance = right.distanceMiles ?? Number.POSITIVE_INFINITY;
      return leftDistance - rightDistance;
    })
    .slice(0, Math.min(Math.max(limit, 0), 3));
}

export default function MerchantFeatureBand({ mode }: { mode: BrowseMode }) {
  const { location, hydrated } = useLocation();
  const normalizedLocation = useMemo(
    () => getNormalizedLocation(location || {}),
    [location]
  );
  const [businesses, setBusinesses] = useState<EditorialBusiness[]>([]);

  useEffect(() => {
    if (!hydrated || !hasUsableLocationFilter(normalizedLocation)) {
      const timeoutId = window.setTimeout(() => setBusinesses([]), 0);
      return () => window.clearTimeout(timeoutId);
    }

    const controller = new AbortController();

    void fetch(
      `/api/public-businesses?${buildBusinessQuery(normalizedLocation)}`,
      {
        signal: controller.signal,
        cache: "no-store",
      }
    )
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || controller.signal.aborted) return;
        setBusinesses(
          selectEditorialBusinesses(
            Array.isArray(payload?.businesses) ? payload.businesses : [],
            normalizedLocation
          )
        );
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          (error as { name?: string } | null)?.name === "AbortError"
        ) {
          return;
        }
        setBusinesses([]);
      });

    return () => controller.abort();
  }, [hydrated, normalizedLocation]);

  const viewAllHref = mode === "customer" ? "/customer/nearby" : "/nearby";

  return (
    <section
      className={`${styles.band} ${styles.merchantBand}`}
      data-testid="merchant-feature-band"
      data-section-tone="sand"
    >
      <div className={`${styles.bandInner} ${styles.mediaRight}`}>
        <LazyAmbientVideo
          src="/videos/homepage/merchant-feature.mp4"
          label="merchant-feature"
          className={`${styles.videoFrame} ${styles.merchantVideo}`}
        />

        <div
          className={styles.bandContent}
          data-has-listings={businesses.length > 0 ? "true" : "false"}
        >
          <p className={styles.eyebrow}>Meet the neighborhood</p>
          <h2 className={styles.heading}>
            The people behind the places you love.
          </h2>
          <p className={styles.description}>
            Discover independent businesses, their products and the stories
            behind them.
          </p>

          {businesses.length > 0 ? (
            <p
              className={styles.inventorySignal}
              data-testid="merchant-inventory-signal"
            >
              {businesses.length} neighborhood{" "}
              {businesses.length === 1 ? "business" : "businesses"}
            </p>
          ) : null}

          {businesses.length > 0 ? (
            <div
              className={styles.businessList}
              aria-label="Featured local businesses"
            >
              {businesses.map((business) => (
                <EditorialBusinessCard
                  key={business.public_id || business.id}
                  business={business}
                />
              ))}
            </div>
          ) : null}

          <Link href={viewAllHref} prefetch={false} className={styles.textCta}>
            <span className={styles.ctaLabel}>Explore local shops</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
